import { useCallback, useEffect, useState } from 'react';
import { Form, Input } from 'antd';
import { useCountDown } from 'ahooks';
import lookie from 'lookie';

import service from '@/service';
import { Toast } from '@/utils';
import { SELECTED_CUSTOMER_ID, VERIFY_CODE_INTERVAL } from '@/constant';

import { MemberWrapper } from '../member-wrapper';
import { CODE_NEXT_SEND_TIME, StepCompProps } from '../../add-member';
import cx from '../index.module.scss';

// Note： 如果是编辑用户信息，需要需改手机号才回出现验证码图标
interface Props extends StepCompProps {
  isShowCode?: boolean;
  currentIsEdit?: boolean;
  isValidPhone: boolean;
}

const PhoneStep = ({ isShowCode = true, currentIsEdit = false, isValidPhone, ...props }: Props) => {
  const { form, hidden } = props;

  const [countdownTargetDate, setCountdownTargetDate] = useState(null);

  const [countdown] = useCountDown({ targetDate: countdownTargetDate });

  useEffect(() => {
    const codeNextSendTime = lookie.get(CODE_NEXT_SEND_TIME);
    if (codeNextSendTime) {
      const countdown = codeNextSendTime - Date.now();
      setCountdownTargetDate(Date.now() + Math.max(countdown, 0));
    }
    return () => {
      lookie.remove(CODE_NEXT_SEND_TIME);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validPhone = useCallback(
    (_, value): Promise<string> => {
      return new Promise(async (resolve, reject) => {
        const phoneReg = isValidPhone ? /^1[3456789]\d{9}$/g : /^\d*$/;

        if (!value?.trim()) {
          return reject('手机号不能为空');
        }
        if (!phoneReg.test(value?.toString())) {
          return reject('请输入正确的手机号');
        }

        service.customer
          .checkCustomerExists({
            phoneNumber: value,
            customerId: currentIsEdit ? lookie.get(SELECTED_CUSTOMER_ID) : 0,
          })
          .then(() => {
            resolve('');
          })
          .catch(({ code, message }) => {
            // Note: 稀有bug，如果接口响应较快错误提示的高度计算有可能出现在键盘下落时，导致显示错误的问题，因此延迟一下保证键盘下落后在提示
            setTimeout(() => {
              if (code === 20000) {
                reject('会员已存在');
              } else {
                reject(message || '服务器错误');
              }
            }, 100);
          });
      });
    },
    [currentIsEdit, isValidPhone]
  );

  const validCode = (_, value) => {
    const emailCodeReg = /^\d{6}$/;
    if (!value?.trim()) {
      return new Promise((resolve, reject) => {
        setTimeout(() => reject('验证码不能为空'), 1000);
      });
    }
    if (!emailCodeReg.test(value)) {
      return new Promise((resolve, reject) => {
        setTimeout(() => reject('请输入正确的验证码'), 1000);
      });
    }
    return new Promise(resolve => {
      setTimeout(() => resolve(''), 1000);
    });
  };

  const sendVerifyCode = useCallback(async () => {
    form
      .validateFields(['phoneNumber'])
      .then(async ({ phoneNumber }) => {
        Toast.loading('正在发送验证码', { duration: 6000000 });
        await service.customer.sendSMSCode(phoneNumber);
        Toast.success('验证码发送成功');
        lookie.set(CODE_NEXT_SEND_TIME, Date.now() + VERIFY_CODE_INTERVAL, { s: VERIFY_CODE_INTERVAL / 1000 });
        setCountdownTargetDate(Date.now() + VERIFY_CODE_INTERVAL);
      })
      .catch(error => {
        console.log('验证手机号信息：', error);
      });
  }, [form]);

  return (
    <>
      <Form.Item
        hidden={hidden}
        name="phoneNumber"
        validateTrigger="onBlur"
        rules={[{ required: true, validator: validPhone }]}
      >
        <MemberWrapper title={'手机号'}>
          <Input placeholder="请输入手机号" allowClear />
        </MemberWrapper>
      </Form.Item>

      {/* 编辑状态才会出现验证码显隐逻辑 */}
      {isShowCode && (
        <Form.Item hidden={hidden} name="code" rules={[{ required: true, validator: validCode }]}>
          <MemberWrapper title={'验证码'}>
            <Input
              placeholder="请输入验证码"
              suffix={
                countdown ? (
                  <div className={cx('countdown')}>{`重新获取（${Math.round(countdown / 1000)}s）`}</div>
                ) : (
                  <div className={cx('get-code')} onClick={sendVerifyCode}>
                    获取验证码
                  </div>
                )
              }
            />
          </MemberWrapper>
        </Form.Item>
      )}
    </>
  );
};

export { PhoneStep };
